<div class="bitacora-chat flex min-h-0 flex-col" [class.h-full]="embedded()">
  @if (!embedded()) {
    <div class="mb-3 mt-4 flex shrink-0 items-center justify-between gap-3">
      <div>
        <h3 class="text-lg font-semibold text-slate-900">Bitácora</h3>
        <p class="text-sm text-slate-500">Correos al cliente y notas internas de seguimiento.</p>
      </div>
      @if (showHeaderActions()) {
        <p-button
          type="button"
          label="Actualizar"
          icon="pi pi-refresh"
          [outlined]="true"
          [loading]="loading()"
          [disabled]="loading()"
          (onClick)="load()"
        />
      }
    </div>
  }

  <div
    class="bitacora-chat__shell flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200"
    [class.shadow-sm]="!embedded()"
  >
    <div
      #chatScroll
      class="bitacora-chat__scroll min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-4"
      (scroll)="onChatScroll($event)"
    >
      @if (loadingMore()) {
        <div class="mb-3 flex justify-center">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs text-slate-600 shadow-sm">
            <i class="pi pi-spin pi-spinner text-sky-600"></i>
            Cargando anteriores…
          </div>
        </div>
      }

      @if (loading() && chatGroups().length === 0) {
        <div class="flex h-full min-h-[12rem] flex-col items-center justify-center gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-600"></i>
          <p class="text-sm text-slate-500">Cargando bitácora…</p>
        </div>
      } @else if (chatGroups().length === 0) {
        <div class="flex h-full min-h-[12rem] flex-col items-center justify-center gap-3 px-6 text-center">
          <span class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 text-slate-400 shadow-sm">
            <i class="pi pi-comments text-xl"></i>
          </span>
          <p class="text-sm font-semibold text-slate-800">Sin mensajes aún</p>
          <p class="max-w-xs text-xs leading-relaxed text-slate-500">
            Los correos enviados y las notas internas aparecerán aquí como una conversación.
          </p>
        </div>
      } @else {
        <div class="mx-auto flex w-full max-w-3xl flex-col gap-3">
          @for (group of chatGroups(); track group.dateKey) {
            <div class="bitacora-chat__day-group flex flex-col gap-3">
              <div class="bitacora-chat__day-sep sticky top-1 z-10 flex justify-center py-1">
                <span class="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200/80">
                  {{ group.title }}
                </span>
              </div>

              @for (row of group.items; track row.id) {
                <article
                  class="bitacora-chat__row"
                  [class.bitacora-chat__row--email]="!isInternal(row)"
                  [class.bitacora-chat__row--internal]="isInternal(row)"
                  (click)="openDetail(row)"
                >
                  <span
                    class="bitacora-avatar"
                    [attr.data-tone]="avatarTone(row)"
                    [attr.title]="authorLabel(row)"
                  >
                    {{ authorInitials(row) }}
                  </span>

                  <div
                    class="bitacora-bubble"
                    [attr.data-channel]="channelKey(row.channel)"
                    [attr.data-status]="timelineStatusKey(row.status)"
                  >
                    <div class="bitacora-bubble__meta">
                      <span class="bitacora-bubble__author">{{ authorLabel(row) }}</span>
                      <span class="bitacora-channel-chip" [attr.data-channel]="channelKey(row.channel)">
                        <i [class]="channelIcon(row.channel)"></i>
                        {{ channelLabel(row.channel) }}
                      </span>
                    </div>

                    @if (row.subject) {
                      <p class="bitacora-bubble__subject">{{ row.subject }}</p>
                    }

                    <div class="bitacora-bubble__body">
                      @if (isInternal(row)) {
                        <p class="whitespace-pre-wrap">{{ previewText(row) }}</p>
                      } @else {
                        <p>{{ previewText(row) }}</p>
                        @if (row.toEmail) {
                          <p class="bitacora-bubble__to">Para: {{ row.toEmail }}</p>
                        }
                        @if (row.attachments?.length) {
                          <p class="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                            <i class="pi pi-paperclip text-[10px]"></i>
                            {{ attachmentSummary(row) }}
                          </p>
                        }
                      }
                    </div>

                    <div class="bitacora-bubble__footer">
                      <span class="bitacora-bubble__time">
                        {{ communicationInstant(row) ? (communicationInstant(row) | date:'HH:mm') : '—' }}
                      </span>
                      @if (!isInternal(row)) {
                        <span
                          class="bitacora-bubble__status"
                          [attr.data-status]="deliveryStatusKey(row)"
                          [attr.title]="deliveryStatusLabel(row)"
                        >
                          @if (isOpened(row)) {
                            <span class="bitacora-checks bitacora-checks--read" aria-hidden="true">
                              <i class="pi pi-check"></i>
                              <i class="pi pi-check"></i>
                            </span>
                            Abierto
                          } @else if (row.status === 'SENT') {
                            <span class="bitacora-checks" aria-hidden="true">
                              <i class="pi pi-check"></i>
                            </span>
                            Enviado
                          } @else {
                            <i [class]="timelineStatusIcon(row.status)"></i>
                            {{ statusLabel(row.status) }}
                          }
                        </span>
                      }
                    </div>
                  </div>
                </article>
              }
            </div>
          }

          @if (!hasMore() && communications().length > 0) {
            <p class="pb-1 pt-2 text-center text-[11px] text-slate-400">Inicio de la conversación</p>
          }
        </div>
      }
    </div>

    @if (allowInternalNotes()) {
      <div class="bitacora-composer shrink-0 border-t border-slate-200 bg-white/95 px-3 py-3 sm:px-4">
        <div class="w-full">
          <div class="mb-1.5 flex items-center gap-2">
            <span class="bitacora-channel-chip" data-channel="internal">
              <i class="pi pi-lock"></i>
              Nota interna
            </span>
            <span class="text-[11px] text-slate-400">Solo visible en la app</span>
          </div>
          <div class="bitacora-composer__field">
            <textarea
              pTextarea
              class="w-full"
              rows="2"
              [ngModel]="internalNoteDraft()"
              (ngModelChange)="internalNoteDraft.set($event)"
              (keydown.enter)="onComposerEnter($event)"
              [autoResize]="true"
              placeholder="Escribe una nota de seguimiento…"
              [disabled]="savingInternal()"
            ></textarea>
            <div class="bitacora-composer__send-wrap">
              <p-button
                type="button"
                icon="pi pi-send"
                [rounded]="true"
                [loading]="savingInternal()"
                [disabled]="!canSaveInternalNote()"
                appDisableUnless="COMMUNICATION_WRITE"
                pTooltip="Guardar nota"
                tooltipPosition="top"
                (onClick)="saveInternalNote()"
              />
            </div>
          </div>
        </div>
      </div>
    }
  </div>
</div>

<p-drawer
  [visible]="detailDrawerVisible()"
  position="right"
  appendTo="body"
  (visibleChange)="onDetailDrawerVisibleChange($event)"
  styleClass="!w-[min(92vw,48rem)]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="min-w-0">
      <h3 class="truncate text-lg font-semibold text-slate-900">
        {{ isInternal(selectedCommunication()) ? 'Detalle de nota interna' : 'Detalle de comunicación' }}
      </h3>
      @if (selectedCommunication(); as item) {
        <p class="truncate text-xs text-slate-500">{{ item.subject }}</p>
      }
    </div>
  </ng-template>
  @if (selectedCommunication(); as item) {
    <div class="flex min-w-0 flex-col gap-4">
      <div class="flex min-w-0 items-center gap-3">
        <span class="bitacora-avatar bitacora-avatar--lg" [attr.data-tone]="avatarTone(item)">
          {{ authorInitials(item) }}
        </span>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-slate-900">{{ authorLabel(item) }}</p>
          <div class="mt-1 flex flex-wrap items-center gap-2">
            <span class="bitacora-channel-chip" [attr.data-channel]="channelKey(item.channel)">
              <i [class]="channelIcon(item.channel)"></i>
              {{ channelLabel(item.channel) }}
            </span>
            @if (!isInternal(item)) {
              <p-tag
                [value]="deliveryStatusLabel(item)"
                [severity]="isOpened(item) ? 'info' : statusSeverity(item.status)"
              />
            }
          </div>
        </div>
      </div>

      <div class="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
        @if (!isInternal(item)) {
          <div class="min-w-0">
            <p class="text-xs font-medium text-slate-500">Plantilla</p>
            <p class="text-sm text-slate-700">{{ item.templateName || '—' }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-medium text-slate-500">Para</p>
            <p class="break-all text-sm text-slate-700">{{ item.toEmail || '—' }}</p>
          </div>
          @if (item.ccEmails?.length) {
            <div class="min-w-0 sm:col-span-2">
              <p class="text-xs font-medium text-slate-500">Copia (CC)</p>
              <p class="break-all text-sm text-slate-700">{{ item.ccEmails.join(', ') }}</p>
            </div>
          }
          <div class="min-w-0">
            <p class="text-xs font-medium text-slate-500">Fecha de envío</p>
            <p class="text-sm text-slate-700">
              {{ item.sentAt ? (item.sentAt | date:'dd/MM/yyyy HH:mm') : (item.createdAt ? (item.createdAt | date:'dd/MM/yyyy HH:mm') : '—') }}
            </p>
          </div>
          @if (item.status === 'SENT') {
            <div class="min-w-0">
              <p class="text-xs font-medium text-slate-500">Apertura</p>
              @if (isOpened(item)) {
                <span class="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600">
                  <i class="pi pi-eye text-sm"></i>
                  Abierto
                </span>
              } @else {
                <p-tag value="No abierto" severity="secondary" class="text-xs" />
              }
            </div>
            <div class="min-w-0">
              <p class="text-xs font-medium text-slate-500">Fecha de apertura</p>
              <p class="text-sm text-slate-700">
                {{ item.openedAt ? (item.openedAt | date:'dd/MM/yyyy HH:mm') : '—' }}
              </p>
            </div>
          }
        }
        <div class="min-w-0 sm:col-span-2">
          <p class="text-xs font-medium text-slate-500">Asunto</p>
          <p class="break-words text-sm font-semibold text-slate-900">{{ item.subject || '—' }}</p>
        </div>
        @if (item.errorMessage) {
          <div class="min-w-0 sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Error</p>
            <p class="break-words text-sm text-red-600">{{ item.errorMessage }}</p>
          </div>
        }
        @if (item.attachments?.length) {
          <div class="min-w-0 sm:col-span-2">
            <p class="mb-1 text-xs font-medium text-slate-500">Adjuntos</p>
            <ul class="flex flex-col gap-1">
              @for (file of item.attachments; track file.fileId ?? file.name) {
                <li class="inline-flex items-center gap-2 text-sm text-slate-700">
                  <i class="pi pi-paperclip text-xs text-slate-400"></i>
                  <span class="truncate">{{ file.name || 'archivo' }}</span>
                  @if (file.extension) {
                    <span class="text-xs uppercase text-slate-400">{{ file.extension }}</span>
                  }
                  @if (file.size != null) {
                    <span class="text-xs text-slate-400">{{ formatFileSize(file.size) }}</span>
                  }
                </li>
              }
            </ul>
          </div>
        }
        <div class="min-w-0 sm:col-span-2">
          <p class="mb-1 text-xs font-medium text-slate-500">
            {{ isInternal(item) ? 'Nota' : 'Mensaje enviado' }}
          </p>
          @if (isInternal(item)) {
            <div class="max-h-[28rem] overflow-auto whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700">
              {{ item.messageBody }}
            </div>
          } @else {
            <div
              class="communication-preview min-w-0 w-full max-w-full break-words whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700"
              [innerHTML]="item.messageBody"
            ></div>
          }
        </div>
      </div>
    </div>
  }
</p-drawer>
